import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as S from './RightBar.styled';
import Escape from '@mui/icons-material/Clear';
import { ModalParts } from 'pages/redux';
import Modal from '../Modal/Modal';

export const SetModalContext = React.createContext({
  // eslint-disable-next-line no-unused-vars
  setCenterModal: (b: ModalParts | null) => {},
  // eslint-disable-next-line no-unused-vars
  setModal: (b: ModalParts | null) => {},
  // eslint-disable-next-line no-unused-vars
  setSecondModal: (b: ModalParts | null) => {}
});

const RightBar = ({
  modal,
  setModal
}:{
  modal: ModalParts | null,
  // eslint-disable-next-line no-unused-vars
  setModal: (m: ModalParts | null) => void,
}) => {
  const [centerModal, setCenterModal] = useState<ModalParts | null>(null);
  const [secondModal, setSecondModal] = useState<ModalParts | null>(null);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowSecondModal, setIsShowSecondModal] = useState(false);
  const contRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const bookContRef = useRef<HTMLDivElement>(null);
  const bg2Ref = useRef<HTMLDivElement>(null);
  const bookCont2Ref = useRef<HTMLDivElement>(null);

  const setSecondModal0 = (modalParts: ModalParts | null) => {
    if (modalParts) {
      setSecondModal(modalParts);
      setIsShowSecondModal(true);
    } else {
      setIsShowSecondModal(false);
      setTimeout(() => {
        setSecondModal?.(null);
      }, 251);
    }
  };

  // Context setModal must update parent `modal` so in-panel swaps (e.g. booking → token) remount content.
  // Open from parent already sets `modal`; same object ref here is a no-op for useState.
  const setModal0 = (modalParts: ModalParts | null) => {
    if (modalParts) {
      setModal(modalParts);
      setIsShowModal(true);
    } else {
      setIsShowModal(false);
      setSecondModal0(null);
      setTimeout(() => {
        setModal(null);
      }, 251);
    }
  };

  useEffect(() => {
    if (modal) {
      setIsShowModal(true);
    } else {
      setIsShowModal(false);
    }
  }, [modal]);

  useEffect(() => {
    const bg = bgRef.current;
    const cont = contRef.current;
    const bookCont = bookContRef.current;
    if (bg && bookCont && cont) {
      if (isShowModal) {
        bg.style.opacity = '0.6';
        bg.style.pointerEvents = 'auto';
        cont.style.pointerEvents = 'auto';
        bookCont.style.right = '0px';
      } else {
        bg.style.opacity = '0';
        bg.style.pointerEvents = 'none';
        cont.style.pointerEvents = 'none';
        // Prefer CSS off-screen position; offsetWidth can be 0 on first paint (shows panel).
        bookCont.style.right = '';
      }
    }
  }, [isShowModal]);

  useEffect(() => {
    const bg2 = bg2Ref.current;
    const bookCont = bookContRef.current;
    const bookCont2 = bookCont2Ref.current;
    if (bg2 && bookCont && bookCont2) {
      if (isShowSecondModal && isShowModal) {
        bg2.style.opacity = '0.4';
        bg2.style.pointerEvents = 'auto';
        bookCont2.style.transitionDuration = '250ms';
        bookCont.style.maxWidth = '850px';
        bookCont2.style.right = '0px';
      } else {
        bg2.style.opacity = '0';
        bg2.style.pointerEvents = 'none';
        bookCont2.style.transitionDuration = '150ms';
        bookCont.style.maxWidth = '650px';
        bookCont2.style.right = '';
      }
    }
  }, [isShowSecondModal, isShowModal]);

  const renderSecondModal = (children: React.JSX.Element) => {
    return (
      <>
        <S.Bg2 ref={bg2Ref} />
        <S.BookingCont2 ref={bookCont2Ref}>
          <S.Escape onClick={() => setSecondModal0(null)}>
            <Escape fontSize="small" />
          </S.Escape>
          <SetModalContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
              setCenterModal: (b: ModalParts | null) => setCenterModal(b),
              setModal: (b: ModalParts | null) => setModal0(b),
              setSecondModal: (b: ModalParts | null) => setSecondModal0(b)
            }}
          >
            { children }
          </SetModalContext.Provider>
        </S.BookingCont2>
      </>
    );
  };

  if (!modal && !isShowModal && !centerModal && !secondModal && !isShowSecondModal) {
    return null;
  }

  return createPortal(
    <S.Container ref={contRef}>
      {
        centerModal && (
          // Center modal portals outside the side-panel Provider — wrap so Cancel/close work
          <SetModalContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
              setCenterModal: (b: ModalParts | null) => setCenterModal(b),
              setModal: (b: ModalParts | null) => setModal0(b),
              setSecondModal: (b: ModalParts | null) => setSecondModal0(b)
            }}
          >
            <Modal
              component={centerModal.component}
              exitOnBgClick={centerModal.exitOnBgClick}
            />
          </SetModalContext.Provider>
        )
      }
      <S.Bg ref={bgRef} onClick={() => setModal0(null)} />
      <S.BookingCont ref={bookContRef}>
        <S.Escape onClick={() => setModal0(null)}>
          <Escape fontSize="small" />
        </S.Escape>
        <SetModalContext.Provider
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            setCenterModal: (b: ModalParts | null) => setCenterModal(b),
            setModal: (b: ModalParts | null) => setModal0(b),
            setSecondModal: (b: ModalParts | null) => setSecondModal0(b)
          }}
        >
          {modal?.component || <></>}
        </SetModalContext.Provider>
      </S.BookingCont>
      {renderSecondModal(secondModal?.component || <></>)}
    </S.Container>,
    document.body,
  );
};

export default RightBar;
